import { PopUpHandler } from '../../../shared/PopupHandler';
import { FC, useState, useEffect, useRef, ReactNode } from 'react';
import { fetchUploaderIframeURL } from './service';
import { useUserContext } from '../../../../providers/UserProvider';
import { Button } from '../../../shared/Button';
import { LogoSpinner } from '../../../shared/LogoSpinner/LogoSpinner';
import { Prompt } from '../../../shared/Prompt';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import {
  useTagsObjectQuery,
  TagsObjectQuery,
} from '../../../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import './DocumentUploader.scss';
import { xTenantId } from '../../../../consts';
import { Paragraph } from '../../../shared/Paragraph';

interface AddressVerificationConfig {
  merchantkey: string;
  name: string;
  surname: string;
  middlename?: string;
  street: string;
  city: string;
  postcode: string;
  country: string;
  countrycode: string;
  locale: string;
  reference: string;
  onInit: (data: never) => void;
  onComplete: (data: never) => void;
  onFail: (data: never) => void;
}

type DocumentState =
  | 'closed'
  | 'showPrompt'
  | 'idLoading'
  | 'idVerification'
  | 'idError'
  | 'addressLoading'
  | 'addressVerification'
  | 'addressError'
  | 'complete';

const UPLOAD_COMPLETED_KEY = 'doc_upload_completed';

export const DocumentUploader: FC = () => {
  const [state, setState] = useState<DocumentState>('closed');
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [addressVerificationInitialized, setAddressVerificationInitialized] = useState(false);

  const { userProfileId, compliance, user } = useUserContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [addressVerificationElement, setAddressVerificationElement] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslations();
  const isDocUploaderEnabled = JSON.parse(process.env.REACT_APP_ENABLE_DOCUPLOAD as string);

  const { data } = useTagsObjectQuery({
    variables: {
      xTenantId,
      externalResourceId: userProfileId,
      externalResource: 'UserProfileId',
    },
  });

  const hasRequiredTag = (tags: TagsObjectQuery['tagsObject']): boolean => {
    if (!tags) return false;

    return tags.some((tagObj) => tagObj.tag?.name === '2KDOCSREQUIRED' || tagObj.tag?.name === '2KNotice');
  };

  const shouldRunDocUploader = (): boolean => {
    if (!isDocUploaderEnabled || !compliance) return false;

    if (localStorage.getItem(UPLOAD_COMPLETED_KEY) === 'true') return false;

    return compliance.kycLevel === 0 || (compliance.kycLevel === 1 && hasRequiredTag(data?.tagsObject));
  };

  useEffect(() => {
    console.log('[DocumentUploader] STATE CHANGED:', state);
  }, [state]);

  useEffect(() => {
    if (shouldRunDocUploader() && state === 'closed') {
      setState('showPrompt');
    }
  }, [compliance, data, isDocUploaderEnabled, state]);

  const addressVerificationRef = (node: HTMLDivElement | null): void => {
    setAddressVerificationElement(node);
  };

  const fetchIframeUrl = async (): Promise<void> => {
    setState('idLoading');
    try {
      console.log('[DocumentUploader] Fetching iframe URL for userProfileId:', userProfileId);
      const url = await fetchUploaderIframeURL(userProfileId);

      console.log('[DocumentUploader] Iframe URL response:', url);

      setIframeUrl(url + '&embedded=true');
      setState('idVerification');
    } catch (error) {
      console.error('[DocumentUploader] VERIFICATION FAILED - Reason: Failed to fetch iframe URL', error);
      setState('idError');
    }
  };

  useEffect(() => {
    if (state === 'idLoading' && !iframeUrl) {
      fetchIframeUrl();
    }
  }, [state, iframeUrl]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      console.log('[DocumentUploader] ===== MESSAGE EVENT RECEIVED =====');
      console.log('[DocumentUploader] Origin:', event.origin);
      console.log('[DocumentUploader] Data:', event.data);
      console.log('[DocumentUploader] Data type:', typeof event.data);
      console.log('[DocumentUploader] Data keys:', event.data ? Object.keys(event.data) : 'no data');
      console.log('[DocumentUploader] Current state:', state);
      console.log('[DocumentUploader] Timestamp:', new Date().toISOString());
      console.log('[DocumentUploader] =================================');

      const allowedOrigin =
        process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION' ? 'agechecked.getid.ee' : 'agechecked.sb.getid.dev';

      console.log('[DocumentUploader] Expected origin contains:', allowedOrigin);
      console.log('[DocumentUploader] Origin includes allowed?', event.origin.includes(allowedOrigin));
      console.log('[DocumentUploader] Has getidEventName?', 'getidEventName' in event.data);

      if (!event.origin.includes(allowedOrigin) || !('getidEventName' in event.data)) {
        console.log('[DocumentUploader] Message ignored - does not meet criteria');

        return;
      }

      const { getidEventName } = event.data;

      console.log('[DocumentUploader] Valid message - Processing event:', getidEventName);

      if (getidEventName === 'complete') {
        console.log('[DocumentUploader] ID verification COMPLETE event received');
        console.log('[DocumentUploader] Transitioning state: idVerification -> addressLoading');
        setState('addressLoading');
      }

      if (getidEventName === 'fail') {
        console.error('[DocumentUploader] ID verification FAILED event received');
        console.error('[DocumentUploader] Full event data:', JSON.stringify(event.data, null, 2));
        setState('idError');
      }
    };

    console.log('[DocumentUploader] Message event listener ATTACHED');
    window.addEventListener('message', handleMessage);

    return () => {
      console.log('[DocumentUploader] Message event listener REMOVED');
      window.removeEventListener('message', handleMessage);
    };
  }, [state]);

  const loadAC9124SDK = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.AC9124) {
        resolve();

        return;
      }

      const script = document.createElement('script');

      script.id = 'ac9124-script';
      script.src = process.env.REACT_APP_AC_SDK_URL as string;
      script.onload = (): void => resolve();
      script.onerror = (): void => reject(new Error('Failed to load AC9124 SDK'));
      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    if (
      (state === 'addressLoading' || state === 'addressVerification') &&
      !addressVerificationInitialized &&
      addressVerificationElement
    ) {
      const initializeAddressVerification = async (): Promise<void> => {
        try {
          await loadAC9124SDK();

          if (window.AC9124 && addressVerificationElement) {
            console.log('[DocumentUploader] Current page origin:', window.location.origin);
            console.log('[DocumentUploader] Initializing AC9124 SDK');

            const config: AddressVerificationConfig = {
              merchantkey: process.env.REACT_APP_AC_MERCHANT_KEY as string,
              name: user?.givenName || '',
              surname: user?.familyName || '',
              street: user?.address?.streetAddress || '',
              city: user?.address?.addressLocality || '',
              postcode: user?.address?.postalCode || '',
              country: user?.address?.addressCountry || '',
              countrycode: 'GB',
              locale: 'en-GB',
              reference: userProfileId,
              onInit: (data) => {
                console.log('[DocumentUploader] AC9124 SDK onInit called', data);
                setAddressVerificationInitialized(true);
                setState('addressVerification');
              },
              onComplete: (data) => {
                console.log('[DocumentUploader] AC9124 SDK onComplete called', data);
                setState('complete');
              },
              onFail: (data) => {
                console.error('[DocumentUploader] VERIFICATION FAILED - Address verification failed', data);
                setState('addressError');
              },
            };

            const ac9124 = new window.AC9124();

            ac9124.init(config);
          }
        } catch (error) {
          console.error(
            '[DocumentUploader] VERIFICATION FAILED - Address verification SDK initialization error',
            error
          );
          setState('addressError');
        }
      };

      initializeAddressVerification();
    }
  }, [state, addressVerificationInitialized, addressVerificationElement, user, userProfileId]);

  const handleStartVerification = (): void => {
    setState('addressLoading');
  };

  const handleClose = (): void => {
    setState('closed');
    localStorage.setItem(UPLOAD_COMPLETED_KEY, 'true');
  };

  const handleRetryId = (): void => {
    setIframeUrl(null);
    setState('idLoading');
  };

  const handleRetryAddress = (): void => {
    setAddressVerificationInitialized(false);
    setState('addressLoading');
  };

  if (!shouldRunDocUploader()) {
    return null;
  }

  const renderContent = (): ReactNode => {
    if (state === 'complete') {
      return (
        <div className="doc-uploader__message doc-uploader__complete">
          <Paragraph>{t('document-uploader-complete-message')}</Paragraph>
        </div>
      );
    }

    if (state === 'addressError') {
      return (
        <div className="doc-uploader__message doc-uploader__complete">
          <Paragraph>
            {t('document-uploader-failed-message')}{' '}
            <Button type="button" variant="text" onClick={handleRetryAddress}>
              {t('document-uploader-try-again')}
            </Button>
          </Paragraph>
        </div>
      );
    }

    if (state === 'addressLoading' || state === 'addressVerification') {
      return (
        <div className="address-verification-container">
          {state === 'addressLoading' && (
            <div className="spinner-wrapper spinner-wrapper--overlay">
              <LogoSpinner />
            </div>
          )}
          <div id="ac-component" ref={addressVerificationRef} />
        </div>
      );
    }

    if (state === 'idError') {
      return (
        <div className="doc-uploader__message doc-uploader__complete">
          <Paragraph>
            {t('document-uploader-failed-message')}{' '}
            <Button type="button" variant="text" onClick={handleRetryId}>
              {t('document-uploader-try-again')}
            </Button>
          </Paragraph>
        </div>
      );
    }

    if (state === 'idLoading') {
      return (
        <div className="spinner-wrapper">
          <LogoSpinner />
        </div>
      );
    }

    return iframeUrl ? (
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title="ID Document Uploader"
        allow="camera; microphone"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-top-navigation"
        onLoad={(): void => {
          console.log('[DocumentUploader] Iframe LOADED');
          console.log('[DocumentUploader] Iframe URL:', iframeUrl);
          console.log('[DocumentUploader] Current state:', state);
        }}
        onError={(e): void => {
          console.error('[DocumentUploader] Iframe ERROR:', e);
        }}
      />
    ) : (
      <div className="spinner-wrapper">
        <LogoSpinner />
      </div>
    );
  };

  const getModalTitle = (): string => {
    if (state === 'addressLoading' || state === 'addressVerification' || state === 'addressError') {
      return 'Proof of Address Verification';
    }

    if (state === 'complete') {
      return 'Document Verification';
    }

    return 'ID Document Upload';
  };

  const isModalOpen = state !== 'closed' && state !== 'showPrompt';

  return (
    <>
      <div className="doc-uploader">
        <Prompt
          PrimaryButton={{ text: 'Start Verification', onClick: handleStartVerification }}
          show={state === 'showPrompt'}
          title="Document Verification Required"
          onClose={handleStartVerification}
        >
          {t('documentUpload.prompt')}
        </Prompt>

        <PopUpHandler
          elementToFocusOnOpen={iframeRef}
          isOpen={isModalOpen}
          header={getModalTitle()}
          onClose={handleClose}
          modalProps={{ className: 'doc-uploader__modal' }}
          drawerProps={{ className: 'doc-uploader__modal' }}
        >
          {renderContent()}
        </PopUpHandler>
      </div>
    </>
  );
};
